import React, { useState, useEffect } from 'react';
import { X, Mail, User, CreditCard, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { StripePrice, ClientInvitationCreate } from '../types';
import apiService from '../services/api';

interface ClientInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ClientInvitationModal: React.FC<ClientInvitationModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<ClientInvitationCreate>({
    email: '',
    name: '',
    stripe_price_id: '',
    is_free: false
  });
  const [subscriptionPlans, setSubscriptionPlans] = useState<StripePrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionPlans();
    }
  }, [isOpen]);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      setError(null);
      
      // Try to get subscription options for EUR first, then USD, then all
      let subscriptionOptions = [];
      try {
        subscriptionOptions = await apiService.getSubscriptionOptions('eur');
      } catch (err) {
        try {
          subscriptionOptions = await apiService.getSubscriptionOptions('usd');
        } catch (err2) {
          // If both fail, try without currency filter
          subscriptionOptions = await apiService.getSubscriptionOptions();
        }
      }
      
      console.log('Subscription options response:', subscriptionOptions);
      
      // Extract all active prices from all subscription options
      const activePlans = subscriptionOptions.flatMap(option => 
        option.prices.filter(price => price.is_active)
      );
      console.log('Active plans:', activePlans);
      
      setSubscriptionPlans(activePlans);
    } catch (err: any) {
      console.error('Error fetching subscription plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please fill in the email address');
      return;
    }

    if (!formData.is_free && !formData.stripe_price_id) {
      setError('Please select a subscription plan or mark as free');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.createClientInvitation(formData);
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
        setSuccess(false);
        setFormData({ email: '', name: '', stripe_price_id: '', is_free: false });
      }, 2000);
      
    } catch (err: any) {
      console.error('Error creating client invitation:', err);
      setError(err.response?.data?.message || 'Failed to create client invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ email: '', name: '', stripe_price_id: '', is_free: false });
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Invite New Client</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {formData.is_free ? 'Free Client Created!' : 'Invitation Sent!'}
              </h3>
              <p className="text-gray-600">
                {formData.is_free 
                  ? 'Your client will receive a welcome email with login instructions.'
                  : 'Your client will receive an email with a secure payment link.'
                }
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="client@example.com"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name (Optional)
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="Client's full name"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Free Client Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_free"
                  name="is_free"
                  checked={formData.is_free}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="is_free" className="ml-2 block text-sm text-gray-700">
                  Add as free client (no payment required)
                </label>
              </div>

              {/* Subscription Plan Selection - Only show if not free */}
              {!formData.is_free && (
                <div>
                  <label htmlFor="stripe_price_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan *
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    {loadingPlans ? (
                      <div className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50">
                        <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                      </div>
                    ) : (
                      <select
                        id="stripe_price_id"
                        name="stripe_price_id"
                        value={formData.stripe_price_id}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        required
                        disabled={loading}
                      >
                        <option value="">Select a subscription plan</option>
                        {subscriptionPlans.map((plan) => (
                          <option key={plan.id} value={plan.stripe_price_id}>
                            {plan.product?.name || 'Unknown Product'} - {plan.formatted_price}
                            {plan.recurring_interval && ` (${plan.recurring_interval})`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {subscriptionPlans.length === 0 && !loadingPlans && (
                    <p className="text-sm text-gray-500 mt-1">
                      No subscription plans available. Please create plans in your Stripe dashboard.
                    </p>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Send className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      {formData.is_free 
                        ? 'Your client will receive a welcome email with login instructions to set up their account.'
                        : 'Your client will receive an email with a secure Stripe checkout link to complete their subscription.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-sm flex items-center space-x-2 disabled:opacity-50"
                  disabled={loading || !formData.email || (!formData.is_free && !formData.stripe_price_id)}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{formData.is_free ? 'Creating...' : 'Sending...'}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>{formData.is_free ? 'Create Free Client' : 'Send Invitation'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientInvitationModal; 